import {
  connectJira,
  disconnectJira,
  getJiraAuthUrl,
  getJiraStatus,
  listJiraSites,
  selectJiraSite,
  type JiraIntegrationStatus,
  type JiraSite,
} from '@/api';
import { extractErrorDetail } from '@/utils/errorHandler';

/**
 * Ask the backend for the Atlassian consent URL.
 *
 * `state` is never stored server-side: we generate it, hand it over so it ends up in the
 * Atlassian URL, and compare what Atlassian echoes back on `/oauth/callback/jira`. The frontend
 * is the only place that CSRF check happens. Never build the Atlassian URL here — the backend
 * owns `client_id`, `scope`, `audience`, `prompt=consent` and `redirect_uri`, and dropping
 * `offline_access` would yield no refresh token.
 */
export async function requestJiraAuthorization(): Promise<{ url: string; state: string }> {
  const state = crypto.randomUUID();
  const response = await getJiraAuthUrl({ query: { state } });

  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to start the Jira connection.'));
  }

  const url = response.data.url;
  if (typeof url !== 'string' || !url.trim()) {
    throw new Error('No authorization URL was returned by the backend.');
  }

  return { url, state: response.data.state ?? state };
}

export async function completeJiraOAuth(code: string): Promise<JiraIntegrationStatus> {
  const response = await connectJira({ body: { code } });

  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to complete the Jira connection.'));
  }

  return response.data;
}

export async function fetchJiraIntegrationStatus(): Promise<JiraIntegrationStatus> {
  const { data } = await getJiraStatus({ throwOnError: true });
  return data;
}

/**
 * The Jira sites the stored consent reaches. Fetched live from Atlassian on every call, so this
 * is slower than the other routes and can fail if the grant died in the meantime.
 */
export async function fetchJiraSites(): Promise<JiraSite[]> {
  const { data } = await listJiraSites({ throwOnError: true });
  return data.sites;
}

export async function selectJiraSiteForOrganisation(
  cloudId: string
): Promise<JiraIntegrationStatus> {
  const response = await selectJiraSite({ body: { cloud_id: cloudId } });

  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to select the Jira site.'));
  }

  return response.data;
}

export async function removeJiraIntegration(): Promise<JiraIntegrationStatus> {
  const { data } = await disconnectJira({ throwOnError: true });
  return data;
}
